import React, { useMemo, useState } from "react";

const money = (v) => `${v >= 0 ? "+" : ""}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { try { return new Date(s + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }); } catch { return s; } };

const EquityCurve = ({ daily = [] }) => {
  const [hover, setHover] = useState(null);
  const W = 720, H = 240, padL = 8, padR = 8, padT = 16, padB = 22;

  const geo = useMemo(() => {
    if (!daily.length) return null;
    const vals = daily.map((d) => d.cumulative);
    const min = Math.min(0, ...vals);
    const max = Math.max(0, ...vals, 0.0001);
    const span = max - min || 1;
    const n = daily.length;
    const x = (i) => padL + (n === 1 ? (W - padL - padR) / 2 : (i / (n - 1)) * (W - padL - padR));
    const y = (v) => padT + (1 - (v - min) / span) * (H - padT - padB);
    const pts = daily.map((d, i) => [x(i), y(d.cumulative)]);
    const line = pts.map((p) => p.join(",")).join(" ");
    const area = `${padL},${y(0)} ${line} ${x(n - 1)},${y(0)}`;
    const zeroY = y(0);
    return { x, y, pts, line, area, zeroY, n };
  }, [daily]);

  if (!geo) return <div className="text-[13px] text-[color:var(--ws-muted)] m-auto">No trades to plot.</div>;

  const last = daily[daily.length - 1]?.cumulative || 0;

  return (
    <div className="relative" data-testid="analytics-equity-curve">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" className="overflow-visible"
        onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={last >= 0 ? "rgba(56,189,248,0.35)" : "rgba(248,113,113,0.32)"} />
            <stop offset="100%" stopColor="rgba(56,189,248,0)" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={padL} x2={W - padR} y1={padT + f * (H - padT - padB)} y2={padT + f * (H - padT - padB)} stroke="rgba(120,140,180,0.08)" strokeWidth="1" />
        ))}
        <line x1={padL} x2={W - padR} y1={geo.zeroY} y2={geo.zeroY} stroke="rgba(120,140,180,0.28)" strokeWidth="1" strokeDasharray="4 4" />
        <polygon points={geo.area} fill="url(#eqFill)" />
        <polyline points={geo.line} fill="none" stroke={last >= 0 ? "#38bdf8" : "#f87171"} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {geo.pts.map((p, i) => (
          <g key={i}>
            {hover === i && <circle cx={p[0]} cy={p[1]} r="4" fill="#fff" stroke={daily[i].cumulative >= 0 ? "#38bdf8" : "#f87171"} strokeWidth="2" />}
            <rect x={p[0] - (W / geo.n) / 2} y={0} width={W / geo.n} height={H} fill="transparent"
              onMouseEnter={() => setHover(i)} style={{ cursor: "crosshair" }} />
          </g>
        ))}
      </svg>
      {hover !== null && daily[hover] && (
        <div className="absolute pointer-events-none z-20 ws-panel px-3 py-2 rounded-lg shadow-xl"
          style={{ left: `${Math.min(80, Math.max(4, (geo.pts[hover][0] / W) * 100))}%`, top: 4, transform: "translateX(-50%)", minWidth: 190, whiteSpace: "nowrap" }}
          data-testid="equity-tooltip">
          <div className="text-[11px] font-semibold text-white mb-1">{fmtDate(daily[hover].date)}</div>
          <div className="flex justify-between gap-4 text-[11px]"><span className="text-[color:var(--ws-muted)]">Day P&L</span><span className={daily[hover].day_pnl >= 0 ? "ws-up" : "ws-down"}>{money(daily[hover].day_pnl)}</span></div>
          <div className="flex justify-between gap-4 text-[11px]"><span className="text-[color:var(--ws-muted)]">Cumulative</span><span className={daily[hover].cumulative >= 0 ? "ws-up" : "ws-down"}>{money(daily[hover].cumulative)}</span></div>
          <div className="flex justify-between gap-4 text-[11px]"><span className="text-[color:var(--ws-muted)]">Trades</span><span className="text-white">{daily[hover].trades}</span></div>
        </div>
      )}
      <div className="flex justify-between text-[10px] text-[color:var(--ws-muted)] mt-1 px-1">
        <span>{fmtDate(daily[0].date)}</span>
        {daily.length > 1 && <span>{fmtDate(daily[daily.length - 1].date)}</span>}
      </div>
    </div>
  );
};

export default EquityCurve;
